<template>
  <div :style="containerStyles"
       class="countdown-container">
    <div v-if="!displayCountdown || !virtual"
         :style="insideStyle"
         class="inside standby">
      <p v-if="firstInLine" class="wait">{{ standByText }}</p>
      <p v-if="!firstInLine"
         :style="{ color }"
         class="queue">
         {{ queuePosition }}
      </p>
    </div>
    <div v-if="displayCountdown && virtual"
         :style="countdownStyle"
         class="inside time">
      <p class="time">{{ time }}</p>
      <p class="meridiem">{{ meridiem }}</p>
      <p class="time-left">{{ timeLeftString }}</p>
    </div>
    <svg :style="containerStyles">
      <circle v-bind="circleProps"
              :stroke="trackerColor">
      </circle>
      <circle v-if="displayCountdown"
              v-bind="circleProps"
              :stroke="color"
              :stroke-dasharray="circumference"
              :stroke-dashoffset="progress">
      </circle>
    </svg>
  </div>
</template>

<script>
  import moment from 'moment';

  export default {
    name: 'CountdownTimer',
    props: {
      radius: {type: Number, default: 80},
      datetime: {type: Object, default: null}, // expects moment
      standByText: {type: String, default: 'Please standby for your turn.'},
      color: {type: String, default: '#736FDC'},
      queuePosition: {type: Number, default: 0},
      virtual: {type: Boolean, default: true},
    },
    data() {
      return {
        circleProps: {
          r: this.radius,
          cx: this.radius + (this.$options.strokeWidth / 2),
          cy: this.radius + (this.$options.strokeWidth / 2),
          'stroke-width': this.$options.strokeWidth,
        },
        trackerColor: `${this.color}35`,
        circumference: 2 * Math.PI * this.radius,
        intervalReference: null,
        timeLeftString: '',
        percentage: 0,
      };
    },
    computed: {
      firstInLine() {
        return this.queuePosition === 0;
      },
      displayCountdown() {
        if (moment().isAfter(this.datetime)) return false;
        return this.datetime && this.percentage !== 100;
      },
      time() {
        return this.datetime.format('hh:mm');
      },
      meridiem() {
        return this.datetime.format('A');
      },
      containerStyles() {
        const width = (this.radius * 2) + (this.$options.strokeWidth);
        return {
          width: `${width}px`,
          height: `${width}px`,
        };
      },
      insideStyle() {
        return {
          padding: `${this.$options.strokeWidth}px`,
        };
      },
      countdownStyle() {
        return {
          color: this.color,
          padding: `${this.$options.strokeWidth}px`,
        };
      },
      progress() {
        return this.circumference - (this.circumference * (this.percentage / 100));
      },
    },
    mounted() {
      this.calculateTimeLeft();
      this.intervalReference = setInterval(() => {
        this.calculateTimeLeft();
      }, 1000);
    },
    destroyed() {
      clearInterval(this.intervalReference);
    },
    methods: {
      calculateTimeLeft() {
        if (this.displayCountdown) {
          const endMilli = this.datetime.valueOf();
          const nowMilli = moment().valueOf();
          const duration = moment.duration(endMilli - nowMilli);

          this.percentage = Math.floor(100 - ((endMilli - nowMilli) / 86400000) * 100); // 86400000 = 24 hrs in ms
          this.timeLeftString = [duration.hours(), duration.minutes(), duration.seconds()]
            .map((number) => number.toString().padStart(2, '0'))
            .join(':');
        }
      },
    },
    strokeWidth: 16,
  }
</script>

<style lang="scss" scoped>
  .countdown-container {
    position: relative;
    display: flex;
  }

  .inside {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    width: 100%;
    text-align: center;

    &.standby {
      .queue {
        font-size: 80px;
        font-weight: font(bold);
      }
    }

    &.time {
      .time {
        font-size: 40px;
        font-weight: font(bold);
        line-height: unset;
      }

      .meridiem {
        font-size: 24px;
        font-weight: font(bold);
        margin-bottom: space(s);
      }
    }
  }

  svg {
    position: absolute;

    circle {
      fill: none;

      &:last-child {
        transform: rotate(-90deg);
        transform-origin: center;
      }
    }
  }
</style>
